import { getPayoutDataSource, isPayoutApiMode, payoutApi } from "./payoutApiClient";

type LooseRecord = Record<string, any>;

export const isPayoutMockMode = () => getPayoutDataSource() !== "api";

/** When false, payout mutations skip OTP / X-Step-Up-Token (local UAT default). */
export const isPayoutStepUpEnabled = () => {
  if (process.env.REACT_APP_PAYOUTS_STEP_UP_ENABLED === "true") return true;
  if (process.env.REACT_APP_PAYOUTS_STEP_UP_ENABLED === "false") return false;
  if (process.env.REACT_APP_STEP_UP_ENABLED === "true") return true;
  if (process.env.REACT_APP_STEP_UP_ENABLED === "false") return false;
  if (isPayoutApiMode() && process.env.NODE_ENV !== "production") return false;
  return true;
};

export const rupeesToPaise = (amount: unknown) => {
  const value = Number(amount);
  return Number.isFinite(value) ? Math.round(value * 100) : 0;
};

export const readSessionJson = <T,>(key: string, fallback: T): T => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
};

export const writeSessionJson = (key: string, value: unknown) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

export const clearTransferSession = () => {
  sessionStorage.removeItem("payoutTransferClientReferenceId");
  sessionStorage.removeItem("payoutTransferCreateResult");
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith("payout-idempotency:create-payment:")) {
      sessionStorage.removeItem(key);
    }
  });
};

export const newTransferClientReferenceId = () =>
  `CLRN-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const compactBody = (body: LooseRecord) =>
  Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );

export const pickString = (value: unknown, keys: string[], fallback = "") => {
  if (!value || typeof value !== "object") return fallback;
  const record = value as LooseRecord;
  for (const key of keys) {
    const next = record[key];
    if (typeof next === "string" && next.trim()) return next.trim();
    if (typeof next === "number") return String(next);
  }
  return fallback;
};

export const stableIdempotencyKey = (scope: string, seed = "current") => {
  const storageKey = `payout-idempotency:${scope}:${seed}`;
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;

  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const key = `${scope}-${random}`;
  sessionStorage.setItem(storageKey, key);
  return key;
};

export const nestedToken = (value: unknown): string | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const record = value as LooseRecord;
  const direct =
    record.step_up_token ?? record.stepUpToken ?? record.token ?? record.verification_token;
  if (typeof direct === "string" && direct) return direct;
  return nestedToken(record.data) ?? nestedToken(record.result);
};

export const verifyStepUp = async ({
  purpose,
  method,
  code,
}: {
  purpose: string;
  method: "authenticator" | "sms";
  code: string;
  /** @deprecated Server derives subject from JWT — ignored */
  subjectId?: string;
}) => {
  const stepUpEnabled = isPayoutStepUpEnabled();

  if (!stepUpEnabled) return undefined;
  if (isPayoutMockMode()) return "mock-step-up-token";

  const channel = method === "sms" ? "SMS" : "TOTP";
  const challenge = await payoutApi.stepUpChallenge(
    compactBody({
      channel,
      purpose,
    }),
    stableIdempotencyKey(`step-up-challenge-${purpose}`, channel),
  );
  const challengeId = pickString(challenge, ["challenge_id", "challengeId", "id"]);
  if (!challengeId) {
    throw new Error("Unable to start verification. Please try again.");
  }

  const response = await payoutApi.stepUpVerify(
    compactBody({
      challenge_id: challengeId,
      code,
    }),
    stableIdempotencyKey(`step-up-verify-${purpose}`, challengeId),
  );
  return nestedToken(response);
};

export const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;
